import React, { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useDialog } from "../components/dialog/DialogContext";
import TableTab from "../components/tabs/table-tab";
import { FILTER_COMMAND, MESSAGE_OVERLOAD_ERROR, MONITORING_COMMAND, MONITORING_COMMAND_STATE } from "../constants/command-status";
import Commands from "../constants/commands";
import DeviceStatus, { DEVICE_STATES2 } from "../constants/device-status";
import { HardwareSubCmds } from "../constants/sub-commands";
import { useLocale } from "../contexts/localeContext";
import { TableContextProvider } from "../contexts/table/TableContextProvider";
import { TranslationKeys } from "../contexts/TranslationKeys";
import { useIcaWebsocketContext } from "../contexts/websocket/IcaWebsocketContext";
import { Category } from "../models/Category";
import columns from '../models/ColumnDefinitions/IOModuleColumns';
import { ExecuteCommand } from "../models/ExecuteCommand";
import { IOModule } from "../models/IOModule";
import { Lookup } from "../models/Lookup";
import LookupTypes from "../models/LookupTypes";
import searchColumns from '../models/search/IOModuleSearch';
import SiteSearch from "../models/search/SiteSearch";
import { DeleteEntityResult } from "../models/services/DeleteEntityResult";
import { executeCommandForDevice } from "../services/executeCommandService";
import { getIOModuleById, getIOModules } from "../services/ioModuleService";
import { getSiteNamesWithPagination } from "../services/siteService";
import { IAppState } from "../store/types";
import { getCommand, getDeviceStates, getWsDevice } from "../utilities/WebsocketUtil";
import { getDeviceTableLegendItems } from "../models/TableLegend";
import { HARDWARE_MODULES } from "../constants/menu-types";

interface Props {
    setBusy: (isBusy: boolean) => void,
    categories: Category[],
    setCategories: (category: Category[]) => void,
    setBusyMessage: (message: string) => void,
}

const IOModulesTab: FC<Props> = ({ setBusy, categories, setCategories, setBusyMessage }) => {
    const { translate } = useLocale();
    const title = translate.byKey(TranslationKeys.ioModules);
    const [lookup, setLookup] = useState<Lookup[]>([]);
    const itemPath = '/access-control/hardware/io-module/';
    const user = useSelector((state: IAppState) => (state.userInfo));
    const [updateTable, setUpdateTable] = useState(() => (updateMethod: (tableData: any[]) => any[]) => { return });
    const [deviceStatusInfo, setDeviceStatusInfo] = useState<{ controllerFixedAddress: string, ioModulesFixedAddresses: string[] }>({
        ioModulesFixedAddresses: [],
        controllerFixedAddress: ""
    });
    const dialog = useDialog();
    const userLoginId = useSelector((state: any) => (state.userInfo.id))

    useEffect(() => {
        updateTable((tableData) => {
            return updateIOModuleState(deviceStatusInfo.ioModulesFixedAddresses, tableData, deviceStatusInfo.controllerFixedAddress)
        })
    }, [deviceStatusInfo])

    const tableDataUpdate = (updateTableMethod: (updateMethod: (tableData: any[]) => any[]) => void) => {
        setUpdateTable(() => updateTableMethod);
    }

    // The device status codes this view is interesting in
    const filterStatusCodes = [DEVICE_STATES2.PERIPHERAL_STATE];

    useEffect(() => {
        setLookup([{
            name: LookupTypes.site,
            values: [],
            dataMethod: getSiteNamesWithPagination,
            searchAccessor: SiteSearch.find(item => item.accessor === "name")?.searchOnName
        }]);
    }, [])

    let filterStatusCodesRequestTimeOut;
    const icaWebSocket = useIcaWebsocketContext();

    useEffect(() => {
        if (icaWebSocket.connectionState) {
            sendfilterStatuCodeRequest();
            icaWebSocket.setOnMessage(onMessage);
            icaWebSocket.startStopTopic(MONITORING_COMMAND.MONITORING_DEVICE, MONITORING_COMMAND.MONITORING, MONITORING_COMMAND_STATE.MONITORING_START);
        }

        return () => {
            clearTimeout(filterStatusCodesRequestTimeOut);
            icaWebSocket.startStopTopic(MONITORING_COMMAND.MONITORING_DEVICE, MONITORING_COMMAND.MONITORING, MONITORING_COMMAND_STATE.MONITORING_STOP);
        }
    }, [icaWebSocket.connectionState])

    const sendfilterStatuCodeRequest = () => {
        // Tell the server to only report on these device status codes.
        const filterStatusCodesRequest = {
            "command": MONITORING_COMMAND.MONITORING_DEVICE,
            "attributes": {
                "name": FILTER_COMMAND.FILTER,
                "statusCodeFilters": {
                    "includeStatusCodes": filterStatusCodes
                }
            }
        }

        filterStatusCodesRequestTimeOut = setTimeout(() => {
            icaWebSocket.sendMessage(JSON.stringify(filterStatusCodesRequest))
        }, 5000)
    }

    const onMessage = (data) => {
        if (!data.data.includes(MESSAGE_OVERLOAD_ERROR)) {
            const response = JSON.parse(data.data);
            if (getCommand(response) === MONITORING_COMMAND.MONITORING_DEVICE) {
                const deviceStates = getDeviceStates(response);

                if (Array.isArray(deviceStates)) {
                    deviceStates.forEach((deviceState) => {
                        const wsDevice = getWsDevice(deviceState);

                        // Peripherals (online readers attached to the controller)
                        if (wsDevice.fixedAddress) {
                            const ioModulesFixedAddresses = wsDevice.peripheralAddresses ? wsDevice.peripheralAddresses : [];
                            setDeviceStatusInfo({
                                ioModulesFixedAddresses: ioModulesFixedAddresses,
                                controllerFixedAddress: wsDevice.fixedAddress
                            })
                        }
                    });
                }
            }
        }
    }

    const updateIOModuleState = (ioModulesFixedAddresses: string[], ioModules: IOModule[], controllerFixedAddress: String) => {
        const ioForThisController = ioModules.filter((ioModule: IOModule) => ioModule.controllerAddress === controllerFixedAddress);

        ioForThisController.forEach((ioModule: IOModule) => {
            let status = DeviceStatus.Unknown.id;

            if (ioModulesFixedAddresses.includes(ioModule.fixedAddress ?? '')) {
                status = DeviceStatus.Online.id;
            }
            else if (!ioModulesFixedAddresses.includes(ioModule.controllerAddress ?? '')) {
                status = DeviceStatus.Offline.id;
            }
            else if (!ioModule.enabled) {
                status = DeviceStatus.Disabled.id;
            }

            // Update the status of the ioModule if it has changed
            if (ioModule.status !== status) {
                const ioModuleToUpdate = ioModules.find((io: IOModule) => io.id === ioModule.id);
                if (ioModuleToUpdate) {
                    ioModuleToUpdate.status = status;
                }
            }
        });

        return ioModules;
    }

    const onDelete = async (id: string): Promise<DeleteEntityResult> => {
        setBusy(true);
        setBusyMessage(translate.byKey(TranslationKeys.unstallingMessage))

        let fixedAddress: string | undefined;

        try {
            const reader = await getIOModuleById(id);
            fixedAddress = reader.fixedAddress
        } catch (err) {
            dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: translate.byKey(TranslationKeys.deviceNotFound) })
        }
        const command: ExecuteCommand = {
            cmd: Commands.Engine.label,
            cmdSubType: HardwareSubCmds.Uninstall_Reader,
            userLoginId: user.id,
            fixedAddress: fixedAddress,
            jsonResponse: ""
        }

        return new Promise((resolve, reject) => {
            executeCommandForDevice(command)
                .then(
                    (deleteResult: any) => {
                        resolve(deleteResult);
                        const newCatergories: Category[] = categories.map((category) => {
                            return category.id === 3 ? { ...category, count: (category?.count ?? 1) - 1 } : category;
                        })
                        setCategories(newCatergories);
                        setBusy(false);
                    },
                    deleteResult => {
                        reject(deleteResult);
                        setBusy(false);
                    })
        });
    }

    const initialiseUserPreferences = (setDetails: (userLoginId, HARDWARE_MODULES) => void) => {
        setDetails(userLoginId, HARDWARE_MODULES);
    }
    return <TableContextProvider
        initialiser={(setDetails) => initialiseUserPreferences(setDetails)}
        dataMethod={getIOModules}
        tableDescriptor={columns}
        searchDescriptor={searchColumns}
        legendItems={getDeviceTableLegendItems(translate)}
        deleteMethod={onDelete}
        useTable
        useSort
        useSearch
        usePagination
        useFilter
        useLegend
        tableDataUpdate={tableDataUpdate}
        lookup={lookup}
        showAddNewButton={false}>
        <TableTab
            title={title}
            selectRows={false}
            rowPath={itemPath}
            cyName="ioModules"/>
    </TableContextProvider>
};

export default IOModulesTab;
